/**
 * Text Segmenter – Teilt bereinigtes HTML in logische Abschnitte.
 * Jedes Segment bekommt eine ID und Metadaten.
 */

/**
 * @typedef {Object} Segment
 * @property {string} id - Eindeutige Segment-ID
 * @property {'heading'|'paragraph'|'heading-with-text'} type - Typ des Segments
 * @property {string} html - HTML-Inhalt zur Anzeige
 * @property {string} plainText - Reintext für TTS
 * @property {number} charCount - Zeichenanzahl des Reintexts
 * @property {number} headingLevel - Heading-Level (1-6) oder 0
 * @property {boolean} [isSplitPart] - true wenn dieses Segment durch 5000-Byte-Splitting entstanden ist
 */

/** Maximum bytes per TTS API call (safe margin under 5000 bytes) */
export const MAX_TTS_BYTES = 4000;

const encoder = new TextEncoder();

/**
 * Berechnet die Byte-Länge eines UTF-8 Strings
 * @param {string} str
 * @returns {number}
 */
export function getByteLength(str) {
  return encoder.encode(str).length;
}

let segmentCounter = 0;

/**
 * Erzeugt eine eindeutige Segment-ID
 * @returns {string}
 */
function generateSegmentId() {
  return `seg-${++segmentCounter}`;
}

/**
 * Extrahiert den Reintext aus einem HTML-String
 * @param {string} html
 * @returns {string}
 */
function getPlainText(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() || '';
}

/**
 * Erkennt ob ein Element eine Überschrift ist und gibt das Level zurück
 * @param {Element} element
 * @returns {number} 0 wenn keine Überschrift, 1-6 für h1-h6
 */
function getHeadingLevel(element) {
  const match = element.tagName?.match(/^H([1-6])$/i);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Teilt bereinigtes HTML in Segmente auf
 * @param {string} cleanHtml
 * @returns {Segment[]}
 */
export function segmentText(cleanHtml) {
  if (!cleanHtml || typeof cleanHtml !== 'string') return [];

  // Reset counter for each new segmentation
  segmentCounter = 0;

  const container = document.createElement('div');
  container.innerHTML = cleanHtml;

  /** @type {Segment[]} */
  const segments = [];

  /**
   * Pending heading that will be grouped with the next paragraph.
   * @type {{ html: string, plainText: string, headingLevel: number } | null}
   */
  let pendingHeading = null;

  /**
   * Flushes a pending heading as a standalone segment
   * (used when two headings follow each other, or heading is at the end)
   */
  function flushPendingHeading() {
    if (pendingHeading) {
      segments.push({
        id: generateSegmentId(),
        type: 'heading',
        html: pendingHeading.html,
        plainText: pendingHeading.plainText,
        charCount: pendingHeading.plainText.length,
        headingLevel: pendingHeading.headingLevel,
      });
      pendingHeading = null;
    }
  }

  /**
   * Creates a segment from a block element, optionally prefixed with a heading.
   * @param {Element} element - The block element (p, div, ul, ol, etc.)
   */
  function createBlockSegment(element) {
    const blockText = element.textContent?.trim() || '';
    if (!blockText) return;

    if (getByteLength(blockText) > MAX_TTS_BYTES) {
      // Split the block text into parts
      const parts = splitTextAtSentenceBoundaries(blockText, MAX_TTS_BYTES);

      // Reconstruct element HTML wrapper for each part
      const tagName = element.tagName.toLowerCase();
      const attrs = Array.from(element.attributes)
        .map((a) => `${a.name}="${a.value}"`)
        .join(' ');
      const attrStr = attrs ? ' ' + attrs : '';

      const htmlParts = parts.map((partText) => `<${tagName}${attrStr}>${partText}</${tagName}>`);

      if (pendingHeading) {
        // Group heading + FIRST part as one combined segment
        const combinedHtml = pendingHeading.html + htmlParts[0];
        const combinedText = pendingHeading.plainText + '. ' + parts[0];
        segments.push({
          id: generateSegmentId(),
          type: 'heading-with-text',
          html: combinedHtml,
          plainText: combinedText,
          charCount: combinedText.length,
          headingLevel: pendingHeading.headingLevel,
        });
        pendingHeading = null;

        // Subsequent parts are paragraphs and mark them as isSplitPart
        for (let j = 1; j < parts.length; j++) {
          segments.push({
            id: generateSegmentId(),
            type: 'paragraph',
            html: htmlParts[j],
            plainText: parts[j],
            charCount: parts[j].length,
            headingLevel: 0,
            isSplitPart: true,
          });
        }
      } else {
        // No pending heading
        parts.forEach((partText, i) => {
          segments.push({
            id: generateSegmentId(),
            type: 'paragraph',
            html: htmlParts[i],
            plainText: partText,
            charCount: partText.length,
            headingLevel: 0,
            isSplitPart: i > 0,
          });
        });
      }
    } else {
      // Fits in one segment
      if (pendingHeading) {
        // Group heading + paragraph as one combined segment
        const combinedHtml = pendingHeading.html + element.outerHTML;
        const combinedText = pendingHeading.plainText + '. ' + blockText;
        segments.push({
          id: generateSegmentId(),
          type: 'heading-with-text',
          html: combinedHtml,
          plainText: combinedText,
          charCount: combinedText.length,
          headingLevel: pendingHeading.headingLevel,
        });
        pendingHeading = null;
      } else {
        // Standalone paragraph
        segments.push({
          id: generateSegmentId(),
          type: 'paragraph',
          html: element.outerHTML,
          plainText: blockText,
          charCount: blockText.length,
          headingLevel: 0,
        });
      }
    }
  }

  // Walk through top-level children
  for (const child of container.children) {
    const headingLevel = getHeadingLevel(child);

    if (headingLevel > 0) {
      // Every heading acts as a separator.
      // If there's already a pending heading, flush it as standalone first.
      flushPendingHeading();

      const text = child.textContent?.trim() || '';
      if (text) {
        // Store heading as pending – it will be grouped with the next paragraph
        pendingHeading = {
          html: child.outerHTML,
          plainText: text,
          headingLevel,
        };
      }
    } else if (child.tagName === 'HR') {
      // Horizontal rules flush the pending heading
      flushPendingHeading();
    } else {
      // Block element (p, div, ul, ol, table, blockquote, pre, etc.)
      // Each block element becomes its own segment
      createBlockSegment(child);
    }
  }

  // Flush remaining pending heading (if heading was at the end with no paragraph)
  flushPendingHeading();

  // If no block-level structure was found, split by double newlines
  if (segments.length === 0 && cleanHtml.trim()) {
    const plainText = getPlainText(cleanHtml);
    const paragraphs = plainText.split(/\n\s*\n/).filter((p) => p.trim());

    if (paragraphs.length > 1) {
      for (const para of paragraphs) {
        const text = para.trim();
        if (text) {
          if (getByteLength(text) > MAX_TTS_BYTES) {
            const parts = splitTextAtSentenceBoundaries(text, MAX_TTS_BYTES);
            parts.forEach((partText, i) => {
              segments.push({
                id: generateSegmentId(),
                type: 'paragraph',
                html: `<p>${partText}</p>`,
                plainText: partText,
                charCount: partText.length,
                headingLevel: 0,
                isSplitPart: i > 0,
              });
            });
          } else {
            segments.push({
              id: generateSegmentId(),
              type: 'paragraph',
              html: `<p>${text}</p>`,
              plainText: text,
              charCount: text.length,
              headingLevel: 0,
            });
          }
        }
      }
    }
  }

  // If still nothing, treat the whole thing as one segment
  if (segments.length === 0) {
    const text = getPlainText(cleanHtml);
    if (text) {
      if (getByteLength(text) > MAX_TTS_BYTES) {
        const parts = splitTextAtSentenceBoundaries(text, MAX_TTS_BYTES);
        parts.forEach((partText, i) => {
          segments.push({
            id: generateSegmentId(),
            type: 'paragraph',
            html: `<p>${partText}</p>`,
            plainText: partText,
            charCount: partText.length,
            headingLevel: 0,
            isSplitPart: i > 0,
          });
        });
      } else {
        segments.push({
          id: generateSegmentId(),
          type: 'paragraph',
          html: cleanHtml,
          plainText: text,
          charCount: text.length,
          headingLevel: 0,
        });
      }
    }
  }

  return segments;
}

/**
 * Splits a plain-text string into chunks that are each at most `maxBytes` long in UTF-8 bytes.
 * Splits are made at the last sentence-ending punctuation (. ! ?) before the limit.
 * If no sentence end is found within the limit, falls back to the last whitespace.
 *
 * @param {string} text
 * @param {number} maxBytes
 * @returns {string[]}
 */
export function splitTextAtSentenceBoundaries(text, maxBytes = MAX_TTS_BYTES) {
  const parts = [];
  let remaining = text.trim();

  while (getByteLength(remaining) > maxBytes) {
    // Get the longest prefix of remaining that fits within maxBytes
    const window = getSubstringFittingBytes(remaining, maxBytes);

    // Find the last occurrence of '. ', '! ', '? ', or sentence-end followed by whitespace/end
    const sentenceEndRegex = /[.!?][\s"'»)\]]*(?=\s)/g;
    let lastMatch = null;
    let match;
    while ((match = sentenceEndRegex.exec(window)) !== null) {
      lastMatch = match;
    }

    let splitAt;
    if (lastMatch !== null) {
      // Split right after the punctuation (include the punctuation in the first chunk)
      splitAt = lastMatch.index + lastMatch[0].length;
    } else {
      // No sentence boundary found – fall back to last whitespace
      const lastSpace = window.lastIndexOf(' ');
      splitAt = lastSpace > 0 ? lastSpace : window.length;
    }

    parts.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }

  if (remaining.length > 0) {
    parts.push(remaining);
  }

  return parts;
}

/**
 * Gibt den maximalen Prefix eines Strings zurück, dessen UTF-8-Länge unter maxBytes liegt.
 * @param {string} text
 * @param {number} maxBytes
 * @returns {string}
 */
function getSubstringFittingBytes(text, maxBytes) {
  if (getByteLength(text) <= maxBytes) return text;

  let charLength = Math.min(text.length, maxBytes);
  let sub = text.slice(0, charLength);
  while (getByteLength(sub) > maxBytes && charLength > 1) {
    const byteLen = getByteLength(sub);
    const diff = byteLen - maxBytes;
    const shrinkBy = Math.max(1, Math.floor(diff / 4));
    charLength -= shrinkBy;
    sub = text.slice(0, charLength);
  }
  return sub;
}

/**
 * Berechnet die Gesamtzeichenanzahl aller Segmente
 * @param {Segment[]} segments
 * @returns {number}
 */
export function getTotalCharCount(segments) {
  return segments.reduce((sum, seg) => sum + seg.charCount, 0);
}

/**
 * Resettet den internen Zähler (nützlich für Tests)
 */
export function resetSegmentCounter() {
  segmentCounter = 0;
}
